import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { PaymentTypeEnum } from '@api/models';
import type { CardPaymentDetailsDto, OrderDto } from '@api/models';
import { ButtonComponent } from '@/components/button/button.component';
import { CardPaymentDetailsComponent } from '@/components/card-payment-details/card-payment-details.component';
import { IconComponent } from '@/components/icon/icon.component';
import { PreloaderComponent } from '@/components/preloader/preloader.component';
import { RouterLinkWithLang } from '@/directives/router-link-with-lang/router-link-with-lang.directive';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';
import { OrderService } from '@/pages/checkout-page/order.service';
import { DeviceService } from '@/services/device-detector/device.service';
import { LanguageService } from '@/services/language/language.service';
import { MonobankPaymentService } from '@/services/monobank-payment.service';
import { WayforpayReturn, WayforpayService } from '@/services/wayforpay.service';

/** Online payment followed by bank accounts in a full panel, or two compact actions in order history. */
@Component({
  selector: 'cmt-order-payment-options',
  templateUrl: './order-payment-options.component.html',
  styleUrls: ['./order-payment-options.component.scss'],
  imports: [
    DecimalPipe,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkWithLang,
    TranslatePipe,
    ButtonComponent,
    CardPaymentDetailsComponent,
    IconComponent,
    PreloaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderPaymentOptionsComponent implements OnInit {
  private readonly wayforpayService = inject(WayforpayService);
  private readonly monobankPaymentService = inject(MonobankPaymentService);
  private readonly orderService = inject(OrderService);
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly languageService = inject(LanguageService);

  readonly order = input.required<Pick<OrderDto, 'id' | 'idExpanded' | 'dueAmount' | 'paidAmount' | 'paymentMethodId'> & {
    prices: Pick<OrderDto['prices'], 'totalCost'>;
  }>();
  readonly layout = input<'panel' | 'buttons'>('panel');
  readonly initialCardDetails = input<CardPaymentDetailsDto | null>(null);
  /** Goes to the card payment page of the order's provider when the parent requests it. */
  readonly payOnlineOnInit = input<boolean>(false);
  /** The result the customer came back from the payment page with. */
  readonly paymentReturn = input<WayforpayReturn | null>(null);

  protected readonly isPaying = signal(false);
  protected readonly payError = signal<string | null>(null);

  protected readonly cardDetails = signal<CardPaymentDetailsDto | null>(null);
  protected readonly isCardDetailsLoading = signal(false);
  protected readonly cardDetailsError = signal<string | null>(null);

  ngOnInit(): void {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    const paymentReturn = this.paymentReturn();
    if (paymentReturn?.status === 'approved') {
      return;
    }
    if (paymentReturn?.status === 'declined') {
      this.payError.set(paymentReturn.message);
    }

    if (this.layout() === 'panel') {
      if (this.initialCardDetails()) {
        this.cardDetails.set(this.initialCardDetails());
      } else {
        this.fetchCardDetails();
      }
    }
    if (this.payOnlineOnInit()) {
      this.payOnlineOnce();
    }
  }

  /**
   * Back from the payment page can load this page anew at the same address and state, which would
   * send the customer straight back there. The history entry remembers the automatic start.
   */
  private payOnlineOnce(): void {
    const orderId = this.order().idExpanded;
    if (history.state?.onlinePaymentOrderId === orderId) {
      return;
    }

    history.replaceState({ ...history.state, onlinePaymentOrderId: orderId }, '');
    this.payOnline();
  }

  payOnline(): void {
    if (this.isPaying()) {
      return;
    }

    this.payError.set(null);
    this.isPaying.set(true);

    const provider = this.order().paymentMethodId === PaymentTypeEnum.ONLINE_PAYMENT_MONOBANK
      ? this.monobankPaymentService
      : this.wayforpayService;

    provider.pay(this.order().idExpanded)
      .pipe(
        finalize(() => this.isPaying.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: error => this.payError.set(resolveHttpErrorMessage(error)),
      });
  }

  private fetchCardDetails(): void {
    this.isCardDetailsLoading.set(true);

    this.orderService.fetchCardPaymentDetails(this.order().idExpanded)
      .pipe(
        finalize(() => this.isCardDetailsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: details => this.cardDetails.set(details),
        error: error => this.cardDetailsError.set(resolveHttpErrorMessage(error)),
      });
  }
}
